/* SUNDAY BATCH · ND-1 — the Document Review page has two sub-tabs.
   Tab "review" (the children): the page as it was — Upload, Documents list, Preview, Extracted fields — unchanged.
   Tab "engagement": "Engagement-level documents" — EngagementDocsSection (the AttachmentPanel contextType "engagement",
   context id = the engagement id, so every existing upload shows; read-only once the engagement is signed off).
   The chosen tab: ?tab=review|engagement opens it and wins; otherwise the tab this user chose last (browser storage keyed by
   the user id — there is no UI-preference route; storage may be missing or blocked: the page then opens on "review").
   A ?highlight=<doc> deep link (global search) needs the documents list, so it opens "review". */
import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import EngagementDocsSection from "@/components/EngagementDocsSection";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";

export const DOC_REVIEW_TABS = ["review", "engagement"];
const KEY = (uid) => `azex.docReviewTab.${uid || "anon"}`;

export function readRememberedTab(uid) {
  try {
    const v = window.localStorage.getItem(KEY(uid));
    return DOC_REVIEW_TABS.includes(v) ? v : null;
  } catch { return null; }
}

function remember(uid, v) {
  try { window.localStorage.setItem(KEY(uid), v); } catch { /* storage blocked: the choice is not remembered */ }
}

export default function DocumentReviewTabs({ engagement, children }) {
  const { t } = useI18n();
  const { user } = useAuth();
  const uid = user?.user_id || user?.id || "";
  const [searchParams, setSearchParams] = useSearchParams();
  const fromUrl = searchParams.get("tab");
  const pick = () => (searchParams.get("highlight") ? "review"
    : DOC_REVIEW_TABS.includes(fromUrl) ? fromUrl : readRememberedTab(uid) || "review");
  const [tab, setTab] = useState(pick);
  useEffect(() => { setTab(pick()); /* a new ?tab= or another user */ // eslint-disable-next-line
  }, [fromUrl, uid]);
  const onChange = (v) => {
    setTab(v);
    remember(uid, v);
    const next = new URLSearchParams(searchParams); next.set("tab", v);
    setSearchParams(next, { replace: true });
  };
  return (
    <Tabs value={tab} onValueChange={onChange} className="space-y-6" data-testid="doc-review-tabs">
      <TabsList>
        <TabsTrigger value="review" data-testid="doc-review-tab-review">{t("docs.title")}</TabsTrigger>
        <TabsTrigger value="engagement" data-testid="doc-review-tab-engagement">{t("dsD.engDocsTitle")}</TabsTrigger>
      </TabsList>
      <TabsContent value="review" className="space-y-6">{children}</TabsContent>
      <TabsContent value="engagement"><EngagementDocsSection engagement={engagement} /></TabsContent>
    </Tabs>
  );
}
